import Backbone from 'backbone'
import OrgGardener from './organizations-gardener'
import _ from 'lodash'
import i18n from 'scoreboard/components/i18n'
import conductor from 'scoreboard/components/conductor'
import EditTreeMenu from './edit-tree-menu'
import globals from 'scoreboard/components/globals'
import Organization from './organization'
import ProjectOrganizationNav from 'scoreboard/components/project-organization-nav'
import Tooltip from 'scoreboard/components/tooltip'
import permissions from 'scoreboard/components/permissions'
import noOrgsViewable from './no-orgs-viewable.hbs'
import loadingDots from 'scoreboard/components/loading-dots.hbs'
import appDisplay from 'scoreboard/components/app-display'
import NewForm from './new-form'
import HttpStatusCodes from 'http-status-codes'
import guard from 'scoreboard/components/unsaved/guard'
import requiresOrg from 'scoreboard/components/requires-org'
import TreeActions from 'scoreboard/components/tree-actions'
import orgUpdates from './org-updates'
import sections from 'scoreboard/components/sections'
import config from 'scoreboard/components/config'
import projects from 'scoreboard/projects/projects'
import treeSomeAncestors from 'scoreboard/components/tree-some-ancestors'
import treeSomeDescendants from 'scoreboard/components/tree-some-descendants'
import highlight from 'scoreboard/components/highlight'

var OrganizationsNav = ProjectOrganizationNav.extend({

	className : 'org-pane',

	section: 'organizations',

	displayTreeTopToolbar: _.constant(false),

	Model: Organization,
	Gardener: OrgGardener,

	EditTreeMenu: EditTreeMenu,

	events: _.extend({}, ProjectOrganizationNav.prototype.events, {
		'click .create-first': 'createFirst'
	}),

	initialize() {
		this.initialRequestedUri = window.requestedUri  // must go before ProjectOrganizationNav.initialize()
		ProjectOrganizationNav.prototype.initialize.apply(this, arguments)
	},

	_gardener: function() {
		return new this.Gardener({
			// #9559 globals.orgId may already be set so be sure to use that
			// as a default
			id: this.options.id || globals.orgId,
			projectId: this.options.projectId || projects.id,
			type: 'organizations',
			section: this._currentSection(),
			permissions: true,
			copyInfo: true,
			params: {
				pendingDeletion: true
			},
			finderOptions: {
				type: this.options.searchType || 'dark'
			}
		})
	},

	/**
	 * @override
	 */
	selectCurrent() {
		// No tree for exports
		_.invoke(this.gardener.tree, 'select', globals.orgId)
	},

	_currentSection: function () {
		return this.options.section || conductor.section() || sections.default()
	},

	_bindEvents: function() {
		ProjectOrganizationNav.prototype._bindEvents.apply(this, arguments)

		this.listenTo(Backbone, 'change:org', this._changeOrg.bind(this))
		// if we just loaded a new org tree, make sure it's displayed instead of
		// the "create organization" button
		this.listenTo(Backbone, 'load:orgTree', this.toggleTree.bind(this, true))
		this.listenTo(this.gardener, 'end', this._changeOrg.bind(this))
		this.listenTo(this.gardener, 'toggleCollapse', () => this.$el.trigger('toggleCollapse'))

		//Don't auto select/set the org if we're being directed to a specific object on login
		if (_.isNil(this.initialRequestedUri)) {
			this.gardener._onceTreeRendered(() => {
				let orgId = this.gardener.breadthFirstViewableId
				if (projects.size() > 1 || _.isNil(orgId)) return
				if (this.gardener.viewableCount == 1) {
					//#19396, if user can only see one org, automatically set it as the chosen org
					globals.orgId = orgId
				} else {
					//#19396 auto-expand to highest viewable root if user can only see a single branch
					//(e.g. user has view permission to one org with cascade that has children)
					let node
					let tree = this.gardener.tree
					do {
						node = tree.nextSibling(orgId) || tree.previousSibling(orgId)
						orgId = _.get(this.gardener.tree.parent(orgId), 'id', null)
					} while (_.isNil(node) && !_.isNil(orgId))
					if (_.isNil(node)) {
						this._changeOrg(this.gardener.breadthFirstViewableId)
					}
				}
			})
		}

		this.listenTo(this.gardener, 'success', () => {
			let tree = this.gardener.tree
			tree.on('dndstart', evt => {
				evt.data.unreplicatedChildren = treeSomeDescendants(tree, evt.data.id, node => !node.replicated)
			})
			tree.on('dndmove', (info) => {
				//#21478 prevent dropping unreplicated onto trees where replication is cascaded on the low side
				if (config.replicationOutputEnabled && (!info.data.replicated || info.data.unreplicatedChildren)) {
					let isIllegalFromCascade = treeSomeAncestors(tree, info.data.id, (parent) => parent.replicatedCascaded)
					if (isIllegalFromCascade) {
						let parent = tree.get(info.layout.parent.id)
						let content = i18n.get('common.error.sourceOrgRequiresReplication',
							highlight(info.data.label), highlight(parent.label))
						if (this._preventDndTooltip) {
							this._preventDndTooltip.content(content)
						} else {
							this._preventDndTooltip = new Tooltip({
								attachTo: info.traveler,
								placement: 'right',
								arrow: 'center',
								extraClass: 'has-error',
								content
							}).render()
						}
					} else {
						this._removePreventDndTooltip()
					}
				}
			})
			tree.on('dndstop', this._removePreventDndTooltip.bind(this))
		})
		this.listenTo(this.gardener, 'error', function(stream) {
			if (stream.statusCode == HttpStatusCodes.FORBIDDEN) {
				this.$el.empty().append(noOrgsViewable())
			}
		})

		this.el.addEventListener('click', this.recordFinderText.bind(this), true)

		this._onceTreeLoaded(data => orgUpdates.listenToTree(this, this._currentSection()))

		// When the global org has been reset to the same org and this nav is open, make sure
		// that org is selected #39120
		this.listenTo(Backbone, 'reset:org', () => this.selectCurrent())
	},

	recordFinderText: function() {
		this.finderText = this.gardener.finder.text()
	},

	/**
	 * ensures the correct org is selected in the nav pane and
	 * triggers 'change:org' event
	 *
	 * Invoked anytime the user selects a new org in the gardener (tree),
	 * or globals.orgId is changed to a new org.
	 *
	 * @param orgId
	 * @param {Boolean} [options.manualSelect=false] true if the org change was a direct result of
	 * a user selection (clicking the 'select' tree node button)
	 */
	_changeOrg: function(orgId, manualSelect) {
		this.gardener._onceTreeRendered(() => {
			orgId = orgId || globals.orgId

			// The org is changing to some other org, go ahead and select it in the tree and trigger change:org
			// to let listeners know of the change.
			if (orgId) {
				this.gardener.tree.select(orgId)
				manualSelect = true // #38500
			} else {
				// #23147 If orgId and globals.orgId are undefined, select the first node
				orgId = this.$('[data-id]').first().data('id')
				this.gardener.tree.select(orgId, { silent: true })
			}

			this.trigger('change:org', {
				orgNode: this.gardener.tree.selected(),
				manualSelect })
		})
	},

	renderTreeActions: function() {
		if (this.treeActions) {
			// When selecting projects trees will change, play it safe and always update the reference
			this.treeActions.setTree(this.gardener.tree)
		} else {
			this.treeActions = new TreeActions({
				el: this.el,
				tree: this.gardener.tree,
				buttonContent: i18n.get('common.select'),
				permissions: true
			}).render()

			// #12579 - Must only set these up once
			this.treeActions.on('selected', () => this.select())
			this.on('search', this.treeActions.onSearch.bind(this.treeActions))
			this.treeActions.checkPermissions()
			// If new organization is added, set the new node's tree-action button text
			this.listenTo(Backbone, 'add:domain-object', model => {
				if (!this._interested(model)) return

				// Set the label-mask text for the newly added node and reselect the parent node.
				this.treeActions.setButtonContent()
				this.gardener.tree.select(model.get('parentId'))
			})

			// #20792 for any programmatic selection, ensure the permissions and
			// checked and the icon content is set
			this.listenTo(this.gardener, 'select', () => {
				this.treeActions.applyExistingSelection(this.$('.node.selected'))
			})
		}
	},

	// Callback when user clicks on the .label-mask
	select: function() {
		guard.conditionallyRun(() => {
			const node = this.applyNodeSelection()
			if (node) {
				this._conditionallyShowTooltip(node)
			}
		})
	},

	applyNodeSelection: function(section) {
		// If there's no selected node, we cannot navigate to it
		var node = _.result(this.gardener, 'tree.selected')
		if (!node) {
			return
		}

		// If we're not in edit mode, perform a simple selection - there's no
		// edit modal to deal with.
		if (!this.gardener.editable()) {
			this.selectViewable(node, section)
			// Let listeners know (menu-manager.js) that we've selected an organization.
			this.trigger('select:org', node.id)
			return
		}

		return node
	},

	selectViewable: function(node, section) {
		// issue 4987, when selecting an already-selected org, don't change
		// what's shown in the nav and display panes
		let isNew = globals.orgId != node.id || this.options.section != conductor.section()
		if (isNew) {
			// issue 6825 record the new user id in case an error occurs
			this._changeOrg(node.id, true)
			var id = node[this.options.section]
			if (id) {
				conductor.overview(this.options.section, id)
			} else {
				let sectionName = section ?? this.options.section
				var url = 'organizations/' + node.id + '/' + sections.toUrl(sectionName)
				conductor.navigate(url, { trigger: true })
			}
		}
	},

	// #16098 only show a tooltip if the user has permission to do something to the org
	_userHasEditPermission: function() {
		return this.tooltipContent && this.tooltipContent.$el.children().length > 0
	},

	// show a message that the tree is empty, along with a create button
	_emptyTree: function() {
		ProjectOrganizationNav.prototype._emptyTree.call(this, {noCreateOption: !permissions.anyStructure() || config.replicationInputEnabled})
		this.$('.empty-tree').wrap('<div class="org-nav-content-container"></div>')
		this.$('.empty-tree-message').text(i18n.get('common.organization.noOrganizations'))
		this._removeEditTooltip()
	},

	createFirst: function() {
		var newForm = new NewForm({ model: new Organization() })
		appDisplay.dialog(newForm)
		newForm.on('done', () => {
			this.clearLoadingDots()
			this.gardener.clearLoadingDots()
			this.gardener.editable(true)
			// Wait for the tree to be rendered with the newly created node and select it.
			this.gardener._onceTreeRendered(() => {
				let rootNode = _.get(this.gardener, 'tree.root')
				// Although the tree is rendered at this point, it is not yet in the DOM, so wait for it to get there.
				process.nextTick(() => this.selectViewable(rootNode))
			})
		})
	},

	isEditable: function() {
		return permissions.anyStructure() && (!config.replicationInputEnabled || permissions.serverAdmin())
	},

	appendLoadingDots: function() {
		this.$el.append('<div class="org-nav-content-container"> ' + loadingDots() + ' </div>')
	},

	clearLoadingDots: function () {
		ProjectOrganizationNav.prototype.clearLoadingDots.apply(this, arguments)
		this.$('.org-nav-content-container').remove()
	},

	remove: function() {
		ProjectOrganizationNav.prototype.remove.apply(this, arguments)
		this._removeEditTooltip()
		this._removePreventDndTooltip()
		_.invokeMap([this.treeActions], 'remove')
	},

	gardenerError: function(error) {
		ProjectOrganizationNav.prototype.gardenerError.apply(this, arguments)
		// issue 6825 after visiting an org without permission make sure trees
		// are displayed properly
		// issue 6906 set this AFTER calling toggleTree above
		this.showingTree = false
	},

	toggleTree: function(show) {
		ProjectOrganizationNav.prototype.toggleTree.apply(this, arguments)
		if (this.$('.project-name-label').length <= 0) {
			this.$('.finder').after(`<span class="project-name-label">${_.escape(projects.label)}</span>`)
		}
	},

	_deleteDomainObject: function(model) {
		var newSelection = ProjectOrganizationNav.prototype._deleteDomainObject.apply(this, arguments)
		var section = conductor.section()

		//#9759 only navigate if section requires an org
		if (newSelection && requiresOrg(section)) {
			var url = 'organizations/' + newSelection.id + '/' + section
			conductor.navigate(url, { trigger: true })
		}
	},

	_removePreventDndTooltip() {
		this._preventDndTooltip && this._preventDndTooltip.remove()
		delete this._preventDndTooltip
	}

})

export default OrganizationsNav

OrganizationsNav.__filename__ = 'organizations/organizations-nav.js'
